import React, { useCallback, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import { MailInboxAddresseeDTO, MailInboxAddresseeRef } from "../../models/mail-inbox-addressee";
import { Button, Dialog, DialogContent, Grid, TextField, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import DialogActions from "../DialogActions/DialogActions";
import DialogTitle from "../DialogTitle/DialogTitle";
import { MailInboxAddresseesApiService } from "../../services/mail-inbox-addressees/mail-inbox-addressees-api.service";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";

interface Props {
  item: MailInboxAddresseeDTO;
  handleNotify: () => void;
}

interface State {
  open: boolean;
  loading: boolean;
  error: string;
  newNickname: string;
}

const CopyMailInboxAddressee = ({ item, handleNotify }: Props) => {
  const tRP = "components.mail_inbox_addressees_table";
  const { t } = useTranslation();
  const theme = useTheme();

  const INITIAL_STATE: State = useMemo(
    () => ({
      open: false,
      loading: false,
      error: "",
      newNickname: "",
    }),
    []
  );

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const handleSave = useCallback(async () => {
    try {
      setState((state) => ({ ...state, loading: true }));
      const newId = uuid();
      const response = await new MailInboxAddresseesApiService().saveItems([
        {
          ...item,
          nickname: state.newNickname.trim().length > 0 ? state.newNickname.trim() : item.nickname,
          id: newId,
          recipients: [
            ...item.recipients.map((recipient) => ({
              ...recipient,
              mailInboxAdresse: { id: newId } as MailInboxAddresseeRef,
            })),
          ],
        },
      ]);
      if (response.error) {
        setState((state) => ({
          ...state,
          error: response.error,
        }));
      } else {
        setState({ ...INITIAL_STATE });
        handleNotify();
      }
    } catch (error) {
      setState((state) => ({ ...state, error: error as string }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [item, INITIAL_STATE, handleNotify, state.newNickname]);

  return (
    <>
      <Button
        onClick={() => setState((state) => ({ ...state, open: true }))}
        style={{ textTransform: "none", color: theme.palette.text.primary }}
      >
        {`${t(`${tRP}.copy`)}`}
      </Button>
      <Dialog open={state.open}>
        <DialogTitle
          title={`${t(`${tRP}.copy_title`, { nickname: item.nickname })}`}
          handleClose={() => setState({ ...INITIAL_STATE })}
        />
        <LoadingOverlay
          loading={state.loading}
          error={state.error}
        >
          <DialogContent>
            <Grid style={{ display: "flex", gap: "5px", alignItems: "center" }}>
              <Typography>{`${t(`${tRP}.new_nickname`)}`} :</Typography>
              <TextField
                value={state.newNickname}
                onChange={(e) => setState((state) => ({ ...state, newNickname: e.target.value }))}
              />
            </Grid>

            <Typography>{t(`${tRP}.copy_confirmation_message`)}</Typography>
          </DialogContent>
          <DialogActions
            handleCancel={() => setState({ ...INITIAL_STATE })}
            handleSave={handleSave}
            customSaveName={`${t(`common_words.yes`)}`}
          />
        </LoadingOverlay>
      </Dialog>
    </>
  );
};

export default CopyMailInboxAddressee;
